import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} AutosuggestConfig
 * @property {boolean} enabled
 * @property {string} [inputSelector]
 * @property {string} [bindSelector]
 * @property {string} [resultsId]
 * @property {number} minChars
 * @property {number} debounceDelay
 * @property {number} keywordCount
 * @property {number} productCount
 * @property {number} trendingCount
 * @property {number} topQueriesCount
 * @property {number} infieldCount
 * @property {number} promotedCount
 */

/** @returns {any} Theme-settings config emitted by snippets/unbxd-integration.liquid */
const getConfig = () => (typeof window !== 'undefined' && /** @type {any} */ (window).UNBXD_CONFIG) || {};

/** Default input the controller binds to (header search bar). */
const DEFAULT_BIND_SELECTOR = '#header-main';

/** Id + class of the suggestion box, styled by assets/unbxd-autosuggest.css. */
const RESULTS_ID = 'unbxd-autosuggest-results';
const BOX_CLASS = 'unbxd-as-box';

/** Shared placeholder image for products without an image (parity with assets/unbxd-search.js). */
const UNBXD_PLACEHOLDER_IMG =
  'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

/**
 * Fields requested for autosuggest popular products (`popularProducts.fields`).
 * Product-level entries first, then the variant-level fields the row renders
 * (`v_meta_name` / `v_imageUrl` / `v_productUrl`) so a SKU or variant query shows
 * the variant that actually matched. `variants` must be listed for the nested
 * array itself to survive the field restriction.
 * @type {string[]}
 */
const UNBXD_AS_PRODUCT_FIELDS = [
  'title', 'uniqueId', 'imageUrl', 'price', 'productUrl', 'autosuggest', 'handle',
  'variants', 'variantId', 'v_sku', 'v_title', 'v_meta_name', 'v_imageUrl', 'v_productUrl',
];

/** Default Unbxd search endpoint when none is configured. */
const DEFAULT_ENDPOINT = 'https://search.unbxd.io';

/** Wildcard query for the initial (on-load) browse request. */
const INITIAL_QUERY = '*';

/** Transient markup shown while the initial request is still in flight. */
const LOADING_HTML = '<div class="unx-loading" role="status" aria-live="polite">Loading…</div>';

/** @param {unknown} value @returns {any[]} */
const toArray = (value) => (Array.isArray(value) ? value : []);

/** @param {unknown} value @returns {string} */
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** @param {unknown} value @returns {string} */
const escapeAttr = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** @param {unknown} source @returns {string} First usable URL (Unbxd image fields can be arrays). */
const pickImage = (source) => {
  if (Array.isArray(source)) return source[0] || '';
  return typeof source === 'string' ? source : '';
};

/** @param {any} product @returns {string} First usable image URL (imageUrl can be an array). */
const firstImage = (product) => pickImage(product?.imageUrl ?? product?.images);

/**
 * The matching VARIANT for an autosuggest product hit.
 *
 * Unbxd matches at variant level: when the shopper types an SKU (`8586324`) or a
 * variant phrase (`multi color murano`) the response still carries the PARENT
 * product (`title`/`imageUrl`/`productUrl` = the default variant), and the hit
 * that actually matched is the single entry in `variants[]`. Rendering the
 * parent is what made an SKU search show the default variant's image/title and
 * link to the product handle instead of the variant URL, so prefer the variant
 * whenever the response carries one.
 * @param {any} product
 * @returns {any|null}
 */
const matchedVariant = (product) => {
  const list = product?.variants;
  return Array.isArray(list) && list.length ? list[0] : null;
};

/**
 * Product row markup (right column). Rendered as a link so clicks navigate
 * natively. Class names mirror assets/unbxd-autosuggest.css so the look is
 * unchanged from the previous SDK build.
 * @param {any} product
 * @returns {string}
 */
const productItemHtml = (product) => {
  // Variant-level fields win over the parent product (see `matchedVariant`):
  // `v_meta_name` for the name, `v_imageUrl` for the image, `v_productUrl` for
  // the `?variant=` link. Each falls back to the product-level field when the
  // hit is not variant-specific.
  const variant = matchedVariant(product);
  const title = (variant && variant.v_meta_name) || product?.title || product?.autosuggest || '';
  const url =
    (variant && variant.v_productUrl) ||
    product?.productUrl ||
    (product?.handle ? `/products/${product.handle}` : '');
  if (!url) return '';
  const image =
    (variant && pickImage(variant.v_imageUrl)) || firstImage(product) || UNBXD_PLACEHOLDER_IMG;
  return (
    `<a class="unx-item unx-popular-product" href="${escapeAttr(url)}">` +
    `<img src="${escapeAttr(image)}" alt="${escapeAttr(title)}" loading="lazy" width="72" height="72"` +
    ` onerror="this.onerror=null;this.src=&#39;${UNBXD_PLACEHOLDER_IMG}&#39;;">` +
    `<div>${escapeHtml(title)}</div>` +
    `</a>`
  );
};

/**
 * Keyword / in-field / promoted / top-query / trending row (left column).
 * `data-action="search"` is consumed by the box click delegation.
 * @param {any} item
 * @param {string} doctype Label used for the `unx-*` class (kept identical to the
 *   previous build so the CSS selectors still match, e.g. infield indentation).
 * @returns {string}
 */
const suggestionItemHtml = (item, doctype) => {
  const value = item?.autosuggest || item?.value || '';
  if (!value) return '';
  const className = `unx-item unx-${doctype.replace(/_/g, '-')}`;
  return (
    `<div class="${className}" data-doctype="${doctype}" data-action="search"` +
    ` data-value="${escapeAttr(value)}" role="option" tabindex="-1">${escapeHtml(value)}</div>`
  );
};

/** @param {any[]} products @returns {string} Product `<a>` rows (no column wrapper). */
const productRowsHtml = (products) => products.map(productItemHtml).filter(Boolean).join('');

/** @param {any[]} products @returns {string} Full `.unx-popular-products` column (or ''). */
const productsColumnHtml = (products) => {
  const rows = productRowsHtml(products);
  return rows ? `<div class="unx-popular-products">${rows}</div>` : '';
};

/**
 * Doctypes of the suggestion modules, mapped to the `unx-*` class suffix the
 * markup uses. Unbxd is inconsistent about singular/plural across modules and
 * SDK versions, so every spelling we have seen is accepted rather than silently
 * dropped by the `default:` branch of the render switch.
 * @type {Record<string, string>}
 */
const SUGGESTION_DOCTYPES = {
  KEYWORD_SUGGESTION: 'keyword_suggestion',
  KEYWORD_SUGGESTIONS: 'keyword_suggestion',
  IN_FIELD: 'infield_suggestion',
  IN_FIELDS: 'infield_suggestion',
  PROMOTED_SUGGESTION: 'promoted_suggestion',
  PROMOTED_SUGGESTIONS: 'promoted_suggestion',
  TOP_QUERIES: 'top_search_query',
  TOP_SEARCHES: 'top_search_query',
  TOP_SEARCH_QUERIES: 'top_search_query',
  TRENDING_QUERIES: 'trending_search',
  TRENDING_SEARCH: 'trending_search',
  TRENDING_SEARCHES: 'trending_search',
};

/** Render order of the suggestion groups within the left column. */
const SUGGESTION_ORDER = [
  'keyword_suggestion',
  'infield_suggestion',
  'promoted_suggestion',
  'top_search_query',
  'trending_search',
];

/** @param {any} entry @returns {boolean} True for popular-product entries (no suggestion doctype). */
const isProductEntry = (entry) => !entry?.doctype || !SUGGESTION_DOCTYPES[entry.doctype];

/**
 * Assemble the two-column box markup. Order in the DOM is irrelevant because the
 * CSS reorders the columns via `order` (suggestions left, products right).
 * @param {any[]} products
 * @param {string[]} suggestions
 * @returns {string}
 */
function composeColumns(products, suggestions) {
  const suggestionsMarkup = suggestions.filter(Boolean).join('');
  const productsColumn = productsColumnHtml(products);
  const suggestionsColumn = suggestionsMarkup
    ? `<div class="unx-suggestions">${suggestionsMarkup}</div>`
    : '';
  return `${productsColumn}${suggestionsColumn}`;
}

/**
 * Build the two-column box markup from a direct autosuggest REST response
 * (`response.products[]`).
 *
 * Every module of the V2 response arrives in the SAME flat `products[]` array:
 * suggestion entries carry a `doctype` (KEYWORD_SUGGESTION, IN_FIELD,
 * TOP_SEARCH_QUERIES, PROMOTED_SUGGESTION, TRENDING_QUERIES) while popular
 * products carry none — `popularProducts.fields` does not request `doctype`, so
 * the field restriction strips it from the product documents. Products are
 * therefore everything that is not a known suggestion doctype, and suggestions
 * are grouped so keyword suggestions render first.
 * @param {any} products `data.response.products`
 * @returns {string}
 */
function buildBoxHtml(products) {
  const list = toArray(products);
  const popularProducts = list.filter(isProductEntry);

  /** @type {Record<string, string[]>} */
  const grouped = {};

  for (const entry of list) {
    const group = SUGGESTION_DOCTYPES[entry?.doctype];
    if (!group) continue;
    (grouped[group] ??= []).push(suggestionItemHtml(entry, group));
  }

  const suggestions = SUGGESTION_ORDER.flatMap((group) => grouped[group] ?? []);
  return composeColumns(popularProducts, suggestions);
}

/**
 * `<unbxd-autosuggest>` — header autosuggest controller.
 *
 * Direct-REST build (no SDK). It calls the Unbxd autosuggest endpoint
 * (`{endpoint}/{apiKey}/{siteKey}/autosuggest`) directly so the first suggestion
 * fires the instant the shopper types — there is no SDK bundle to download/parse
 * first. An initial (`q=*`) request is fired on load and cached so the box opens
 * instantly with default content on focus. Rendering reuses the same `unx-*`
 * markup the previous build emitted, so assets/unbxd-autosuggest.css and the
 * layout are unchanged.
 */
class UnbxdAutosuggest extends Component {
  /** @type {boolean} Guards against double initialization across instances. */
  static booted = false;

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {AutosuggestConfig} */
  #config = /** @type {any} */ ({});

  /** @type {HTMLElement | null} The single dropdown box element, reused across inputs. */
  #box = null;

  /** @type {HTMLInputElement | null} The currently engaged search input. */
  #activeInput = null;

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {string} Last query we rendered results for (dedupes repeat fetches). */
  #currentQuery = '';

  /** @type {string} Cached default-panel markup from the initial (`q=*`) request. */
  #initialHtml = '';

  /** @type {boolean} */
  #initialFetched = false;

  /** @type {number} Active keyboard-highlight index within the rendered items. */
  #activeIndex = -1;

  /** @type {number | undefined} Debounce timer for hover-prefetch of products. */
  #hoverTimer;

  /** @type {string} Last suggestion value we loaded hover products for (dedupe). */
  #lastHoverValue = '';

  /** @type {Map<string, string>} Cache of product-row HTML keyed by suggestion value. */
  #hoverCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    if (UnbxdAutosuggest.booted) return;

    const config = /** @type {AutosuggestConfig} */ (getConfig().autosuggest || {});
    if (!config.enabled) return;

    const { siteKey, apiKey } = getConfig();
    if (!siteKey || !apiKey) {
      this.#log('error', 'Missing Unbxd siteKey/apiKey — autosuggest disabled');
      return;
    }

    UnbxdAutosuggest.booted = true;
    this.#config = config;

    // Suppress the theme's native predictive-search panel while Unbxd owns the box.
    document.body.classList.add('unbxd-as-active');

    this.#bindEvents();

    // Bind every search form immediately so blank submit/click is blocked even
    // before the shopper focuses an input.
    document.querySelectorAll(this.#inputSelector()).forEach((el) => {
      if (el instanceof HTMLInputElement) this.#setupForm(el);
    });

    // Fire the initial (browse) request now so the default panel is ready before
    // the shopper engages the box.
    this.#prefetchInitial();

    // This module is a deferred ES module, so on a slow page the shopper can be
    // several characters in before any of the listeners above exist. Those
    // keystrokes fired their `focusin`/`input` events into the void, and nothing
    // would open the box until the shopper typed again. Adopt whatever is
    // already in the field and run it now.
    this.#adoptPreBootInput();

    this.#log('info', 'Unbxd autosuggest (direct REST) initialized');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    window.clearTimeout(this.#debounceTimer);
    window.clearTimeout(this.#hoverTimer);
    document.body.classList.remove('unbxd-as-active');
  }

  /**
   * Pick up a query the shopper typed BEFORE this controller booted.
   *
   * Prefers the focused field; otherwise the first bound input that already
   * carries text (a pre-filled `?q=` bar). Never writes to the input — the
   * shopper's text is the source of truth.
   */
  #adoptPreBootInput() {
    const selector = this.#inputSelector();
    const active = document.activeElement;
    /** @type {HTMLInputElement | null} */
    let input = null;

    if (active instanceof HTMLInputElement && active.closest(selector)) {
      input = active;
    } else {
      for (const el of document.querySelectorAll(selector)) {
        if (el instanceof HTMLInputElement && (el.value || '').trim()) {
          input = el;
          break;
        }
      }
    }

    if (!input) return;

    const query = (input.value || '').trim();
    // Only open the panel for a field the shopper is actually in; a pre-filled
    // bar they have not touched should stay quiet until they focus it.
    if (document.activeElement !== input) {
      this.#syncSubmitEnabled(input);
      return;
    }

    this.#activeInput = input;
    this.#mountBox(input);
    this.#syncSubmitEnabled(input);

    if (query.length >= this.#minChars()) {
      this.#runQuery(query);
    } else {
      this.#showInitial();
    }
  }

  /** @returns {number} */
  #minChars() {
    const value = Number(this.#config?.minChars);
    return value > 0 ? value : 1;
  }

  /** @returns {number} */
  #debounceDelay() {
    const value = Number(this.#config?.debounceDelay);
    return value >= 0 ? value : 300;
  }

  /** @returns {string} CSS selector matching every Unbxd-enabled search input. */
  #inputSelector() {
    return ['.search-input', '#cmdk-input', this.#config?.inputSelector, this.#config?.bindSelector, DEFAULT_BIND_SELECTOR]
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Build the autosuggest REST URL for a query.
   * @param {string} query
   * @returns {string}
   */
  #buildUrl(query) {
    const { apiKey, siteKey, endpoint } = getConfig();
    const base = String(endpoint || DEFAULT_ENDPOINT).replace(/\/+$/, '');
    const cfg = this.#config;
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('version', 'V2');
    // One `<module>.count` per suggestion type the box renders. A count of 0 is a
    // valid "disable this module" instruction, so the fallbacks use `??`.
    params.set('popularProducts.count', String(cfg.productCount ?? 5));
    params.set('keywordSuggestions.count', String(cfg.keywordCount ?? 5));
    params.set('topQueries.count', String(cfg.topQueriesCount ?? 5));
    params.set('inFields.count', String(cfg.infieldCount ?? 5));
    params.set('promotedSuggestion.count', String(cfg.promotedCount ?? 5));
    params.set('trendingQueries.count', String(cfg.trendingCount ?? 5));
    // Variant fields must be named explicitly: `popularProducts.fields` scopes the
    // WHOLE product document, so without `v_*` here the `variants[]` entries come
    // back carrying only the parent's title/imageUrl/productUrl and an SKU search
    // renders the default variant. Requesting a field the feed lacks is harmless.
    params.set('popularProducts.fields', UNBXD_AS_PRODUCT_FIELDS.join(','));
    params.set('variants', 'true');
    params.set('variants.count', '1');
    // Availability must be scoped to the product module. A bare `filter` is applied
    // to EVERY module, and suggestion documents (KEYWORD_SUGGESTION, IN_FIELD,
    // TOP_SEARCH_QUERIES, …) carry no `availability` field at all, so
    // `filter=availability:true` silently drops the entire suggestions column and
    // the response comes back with popular products only.
    params.set('popularProducts.filter', 'availability:true');
    params.set('promotion', 'true');

    return `${base}/${apiKey}/${siteKey}/autosuggest?${params.toString()}`;
  }

  /**
   * @param {string} query
   * @returns {Promise<any>}
   */
  async #fetch(query) {
    const response = await fetch(this.#buildUrl(query), { signal: this.#controller.signal });
    if (!response.ok) throw new Error(`Unbxd autosuggest API error: ${response.status}`);
    return response.json();
  }

  /** Fire the initial (`q=*`) request and cache the default-panel markup. */
  #prefetchInitial() {
    if (this.#initialFetched) return;
    this.#initialFetched = true;
    this.#fetch(INITIAL_QUERY)
      .then((data) => {
        const html = buildBoxHtml(data?.response?.products);
        if (html) this.#initialHtml = html;
        // If the shopper is already focused on an empty field, swap the loading
        // state for the freshly cached default content.
        if (
          this.#activeInput &&
          document.activeElement === this.#activeInput &&
          (this.#activeInput.value || '').trim().length < this.#minChars()
        ) {
          if (this.#initialHtml) {
            this.#render(this.#initialHtml);
          } else {
            this.#hideEmpty();
          }
        }
      })
      .catch((error) => {
        this.#initialFetched = false;
        if (error?.name !== 'AbortError') this.#log('error', error);
      });
  }

  /** Wire delegated focus / input / keydown / outside-click listeners. */
  #bindEvents() {
    const selector = this.#inputSelector();
    const signal = this.#controller.signal;

    document.addEventListener(
      'focusin',
      (event) => {
        const input = /** @type {Element | null} */ (event.target)?.closest?.(selector);
        if (input) this.#onFocus(/** @type {HTMLInputElement} */ (input));
      },
      { capture: true, signal }
    );

    document.addEventListener(
      'input',
      (event) => {
        const input = /** @type {Element | null} */ (event.target)?.closest?.(selector);
        if (input) this.#onInput(/** @type {HTMLInputElement} */ (input));
      },
      { capture: true, signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        const input = /** @type {Element | null} */ (event.target)?.closest?.(selector);
        if (input && input === this.#activeInput) this.#onKeydown(/** @type {KeyboardEvent} */ (event));
      },
      { capture: true, signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#box || !this.#box.isConnected) return;
        const target = /** @type {Element | null} */ (event.target);
        if (!target) return;
        if (this.#box.contains(target)) return;
        if (this.#activeInput && this.#activeInput.contains(target)) return;
        if (target.closest?.(selector)) return;
        this.#hide();
      },
      { signal }
    );

    document.addEventListener(ThemeEvents.sectionMorphed, () => this.#onSectionMorphed(), { signal });
  }

  /**
   * Re-apply our state after a section re-render.
   *
   * `morphSection` rebuilds a section from server markup that knows nothing about
   * this controller: the dropdown is a JS-created element absent from that markup
   * so the morph detaches it, the input's aria wiring is reset, and the submit
   * control regains the enabled state that gates blank searches. The header
   * hydration on every page load goes through this path, so without the recovery
   * below a shopper who was mid-search had the panel close under them.
   *
   * The input itself is keyed by id and morphed in place, so its text, focus and
   * caret survive (see the search-input branch in assets/morph.js) — we only put
   * the controller's own state back on top.
   */
  #onSectionMorphed() {
    let input = this.#activeInput;

    // A replaced (rather than morphed) input leaves us holding a detached node.
    if (input && !input.isConnected && input.id) {
      const replacement = document.getElementById(input.id);
      if (replacement instanceof HTMLInputElement) input = replacement;
    }

    if (!input || !input.isConnected) return;
    this.#activeInput = input;

    this.#setupForm(input);
    this.#syncSubmitEnabled(input);

    // Reopen the panel only for a field the shopper is still focused in. The
    // detached box keeps its markup, so re-inserting it restores the results
    // they were looking at instead of flashing a fresh fetch.
    if (document.activeElement !== input) return;
    if (this.#box && !this.#box.isConnected && this.#box.innerHTML !== '') {
      this.#mountBox(input);
    }
  }

  /** @param {HTMLInputElement} input */
  #onFocus(input) {
    this.#activeInput = input;
    this.#mountBox(input);
    this.#syncSubmitEnabled(input);

    const value = (input.value || '').trim();
    if (value.length >= this.#minChars()) {
      // Pre-filled field: show its results immediately (no debounce).
      this.#runQuery(value);
    } else {
      this.#showInitial();
    }
  }

  /** @param {HTMLInputElement} input */
  #onInput(input) {
    this.#activeInput = input;
    this.#syncSubmitEnabled(input);
    const query = (input.value || '').trim();

    if (query.length < this.#minChars()) {
      window.clearTimeout(this.#debounceTimer);
      this.#currentQuery = '';
      this.#showInitial();
      return;
    }

    if (query === this.#currentQuery) return;

    window.clearTimeout(this.#debounceTimer);
    this.#debounceTimer = window.setTimeout(() => this.#runQuery(query), this.#debounceDelay());
  }

  /**
   * Fetch + render a typed query, ignoring stale responses.
   * @param {string} query
   */
  #runQuery(query) {
    this.#currentQuery = query;
    this.#fetch(query)
      .then((data) => {
        // Stale guard: bail if the shopper has since changed the input.
        if (!this.#activeInput || (this.#activeInput.value || '').trim() !== query) return;
        const html = buildBoxHtml(data?.response?.products);
        if (html) {
          this.#render(html);
        } else {
          this.#hideEmpty();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#log('error', error);
      });
  }

  /** Render the cached default panel (or a loading/empty state). */
  #showInitial() {
    if (this.#initialHtml) {
      this.#render(this.#initialHtml);
      return;
    }
    this.#render(LOADING_HTML);
    this.#prefetchInitial();
  }

  /**
   * Create (once) and insert the dropdown box right after the engaged input, so
   * it shares the same positioned ancestor the previous SDK build used and the
   * CSS positioning is unchanged.
   * @param {HTMLInputElement} input
   */
  #mountBox(input) {
    if (!this.#box) {
      const box = document.createElement('div');
      box.id = RESULTS_ID;
      box.className = BOX_CLASS;
      box.setAttribute('role', 'listbox');
      // Keep the input focused (and the box open) when interacting with the box.
      box.addEventListener('mousedown', (event) => event.preventDefault());
      box.addEventListener('click', (event) => this.#onBoxClick(/** @type {MouseEvent} */ (event)));
      // Hover a left-column suggestion -> prefetch + swap its products into the
      // right column (parity with the SDK's native hover behaviour).
      box.addEventListener('mouseover', (event) => this.#onBoxHover(/** @type {MouseEvent} */ (event)));
      this.#box = box;
    }

    if (input.nextElementSibling !== this.#box) {
      input.insertAdjacentElement('afterend', this.#box);
    }

    input.setAttribute('aria-controls', RESULTS_ID);
    input.setAttribute('aria-owns', RESULTS_ID);
    input.setAttribute('aria-expanded', 'true');

    this.#setupForm(input);
  }

  /**
   * Route form submit + submit-button clicks to the Shopify search page.
   * Blank queries are blocked (no navigation / no native empty submit).
   * @param {HTMLInputElement} input
   */
  #setupForm(input) {
    const form = input.closest('form');
    if (!form || /** @type {any} */ (form).__unbxdAsBound) return;
    /** @type {any} */ (form).__unbxdAsBound = true;

    const submit = (/** @type {Event} */ event) => {
      const query = (input.value || '').trim();
      // Always cancel the native submit — empty searches must not hit /search?q=
      event.preventDefault();
      if (!query) return;
      this.#navigate(query);
    };

    form.addEventListener('submit', submit, { signal: this.#controller.signal });

    const submitButton = form.querySelector('button[type="submit"], [type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', submit, { signal: this.#controller.signal });
    }

    this.#syncSubmitEnabled(input);
  }

  /**
   * Disable the form's submit control while the query is blank so the icon
   * cannot be clicked / activated until the shopper types a keyword.
   * @param {HTMLInputElement} input
   */
  #syncSubmitEnabled(input) {
    const form = input.closest('form');
    if (!form) return;
    const submitButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('button[type="submit"], [type="submit"]')
    );
    if (!submitButton) return;
    const hasQuery = (input.value || '').trim().length > 0;
    submitButton.disabled = !hasQuery;
    submitButton.setAttribute('aria-disabled', hasQuery ? 'false' : 'true');
  }

  /**
   * Write markup into the box, ensuring it is mounted and visible.
   * @param {string} html
   */
  #render(html) {
    if (!this.#box) return;
    if (this.#activeInput && !this.#box.isConnected) {
      this.#activeInput.insertAdjacentElement('afterend', this.#box);
    }
    this.#box.innerHTML = html;
    this.#box.classList.add(BOX_CLASS);
    this.#activeIndex = -1;
    // A fresh full render replaces the products column, so allow hover to
    // re-trigger a prefetch for whatever the shopper hovers next.
    window.clearTimeout(this.#hoverTimer);
    this.#lastHoverValue = '';
    if (this.#activeInput) this.#activeInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * Hover a left-column suggestion -> (debounced) fetch its popular products and
   * swap them into the right column without disturbing the suggestion list.
   * @param {MouseEvent} event
   */
  #onBoxHover(event) {
    const target = /** @type {Element | null} */ (event.target);
    const item = target?.closest?.('.unx-suggestions [data-action="search"]');
    if (!item) return;

    const value = item.getAttribute('data-value') || '';
    if (!value || value === this.#lastHoverValue) return;
    this.#lastHoverValue = value;

    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.#loadHoverProducts(value), 120);
  }

  /**
   * Fetch (or reuse cached) popular products for a hovered suggestion value and
   * render them into the products column.
   * @param {string} value
   */
  #loadHoverProducts(value) {
    const cached = this.#hoverCache.get(value);
    if (cached !== undefined) {
      if (this.#lastHoverValue === value) this.#updateProductsColumn(cached);
      return;
    }

    this.#fetch(value)
      .then((data) => {
        const rows = productRowsHtml(toArray(data?.response?.products).filter(isProductEntry));
        this.#hoverCache.set(value, rows);
        // Ignore if the shopper has since hovered a different suggestion.
        if (this.#lastHoverValue === value) this.#updateProductsColumn(rows);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#log('error', error);
      });
  }

  /**
   * Replace the contents of the products column (creating it if needed) with the
   * given product-row HTML, leaving the suggestion column untouched.
   * @param {string} rows Product `<a>` rows (no wrapper).
   */
  #updateProductsColumn(rows) {
    if (!this.#box) return;
    const column = this.#box.querySelector('.unx-popular-products');
    if (column) {
      column.innerHTML = rows;
    } else if (rows) {
      this.#box.insertAdjacentHTML('afterbegin', `<div class="unx-popular-products">${rows}</div>`);
    }
  }

  /**
   * Remove the box from the DOM. The CSS forces `display: flex !important` on the
   * mounted box, so detaching it is how we close the panel (parity with the
   * previous SDK unmount).
   */
  #hide() {
    if (this.#box && this.#box.parentNode) this.#box.parentNode.removeChild(this.#box);
    this.#activeIndex = -1;
    if (this.#activeInput) this.#activeInput.setAttribute('aria-expanded', 'false');
  }

  /**
   * Close the panel for a query with no results. The markup is cleared so
   * `#onSectionMorphed` does not re-insert the previous query's results.
   */
  #hideEmpty() {
    if (this.#box) this.#box.innerHTML = '';
    this.#hide();
  }

  /** @param {MouseEvent} event */
  #onBoxClick(event) {
    const target = /** @type {Element | null} */ (event.target);
    const item = target?.closest?.('[data-action]');
    if (!item) return; // product rows are <a href> — let them navigate natively.

    const action = item.getAttribute('data-action');
    const value = item.getAttribute('data-value') || '';
    if (action === 'search') {
      event.preventDefault();
      this.#navigate(value);
    }
  }

  /**
   * Keyboard navigation within the open box.
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    if (!this.#box || !this.#box.isConnected) return;

    const items = /** @type {HTMLElement[]} */ (Array.from(this.#box.querySelectorAll('.unx-item')));

    if (event.key === 'Escape') {
      this.#hide();
      return;
    }

    if (event.key === 'Enter') {
      const item = this.#activeIndex >= 0 ? items[this.#activeIndex] : null;
      if (item) {
        event.preventDefault();
        const action = item.getAttribute('data-action');
        if (action === 'search') {
          this.#navigate(item.getAttribute('data-value') || '');
        } else {
          const href = item.getAttribute('href');
          if (href) window.location.href = href;
        }
        return;
      }
      // No highlighted suggestion — only submit when the input has a keyword.
      // Empty Enter must not navigate to /search?q=
      const query = (this.#activeInput?.value || '').trim();
      event.preventDefault();
      if (query) this.#navigate(query);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!items.length) return;
    event.preventDefault();

    if (event.key === 'ArrowDown') {
      this.#activeIndex = Math.min(this.#activeIndex + 1, items.length - 1);
    } else {
      this.#activeIndex = Math.max(this.#activeIndex - 1, -1);
    }

    items.forEach((item, index) => this.#setItemActive(item, index === this.#activeIndex));

    const active = items[this.#activeIndex];
    if (active) {
      active.scrollIntoView({ block: 'nearest' });
      const value = active.getAttribute('data-value');
      if (value && this.#activeInput) this.#activeInput.value = value;
    }
  }

  /**
   * Apply the existing hover appearance to a keyboard-highlighted item without
   * adding new CSS (inline styles mirror the `:hover` rule in
   * assets/unbxd-autosuggest.css).
   * @param {HTMLElement} item
   * @param {boolean} active
   */
  #setItemActive(item, active) {
    item.setAttribute('aria-selected', active ? 'true' : 'false');
    if (active) {
      item.style.background = '#f5f4ff';
      item.style.color = '#5a3fd4';
    } else {
      item.style.background = '';
      item.style.color = '';
    }
  }

  /** @param {string} query */
  #navigate(query) {
    const trimmed = (query || '').trim();
    if (!trimmed) return;
    if (this.#activeInput) this.#activeInput.value = trimmed;
    const searchPath =
      typeof Theme !== 'undefined' && Theme.routes?.search_url ? Theme.routes.search_url : '/search';
    window.location.href = `${searchPath}?q=${encodeURIComponent(trimmed)}`;
  }

  /**
   * @param {'info' | 'error'} level
   * @param {...unknown} args
   */
  #log(level, ...args) {
    if (level === 'error') {
      console.error('[Unbxd:autosuggest]', ...args);
    }
  }
}

if (!customElements.get('unbxd-autosuggest')) {
  customElements.define('unbxd-autosuggest', UnbxdAutosuggest);
}

// Self-host: guarantee a single controller element exists regardless of where
// the integration snippet renders (head/body), without per-form duplication.
function ensureHost() {
  if (document.querySelector('unbxd-autosuggest')) return;
  if (!getConfig().autosuggest?.enabled) return;
  document.body.appendChild(document.createElement('unbxd-autosuggest'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureHost, { once: true });
} else {
  ensureHost();
}
